import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from "react-router-dom";

import {
  Campaign,
} from "@dndModels";

import API from "@utilities/smart_api";

import CampaignParent from '@dndComponents/campaign/CampaignParent';
import LoadingBox from '@components/input/LoadingBox';

const api = API.getInstance();

export default function CampaignDetails() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [obj, setObj] = useState<Campaign | null>(null);
  // Forces a re-render when auth changes, since api.user isn't React state
  const [, setPing] = useState(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!api.user) {
      return;
    }
    if (!id || id === "") {
      // /campaign/create
      setObj(new Campaign());
      return;
    }
    setLoading(true);
    const campaign = await api.getCampaign({ campaign_id: id, include: ["players", "characters"] });
    if (mounted.current) {
      setObj(campaign ? campaign : null);
      setLoading(false);
    }
  }, [id]);

  // On a page refresh the router renders before login finishes, so api.user
  // may still be null here. Wait for the Authenticated event rather than redirecting.
  // Also used for "Reload", which is what spoofing and ending a spoof fire - the
  // campaign needs reloading as whoever the user is now (GM vs player).
  const authChanged = useCallback(() => {
    setPing(p => !p);
    load();
  }, [load]);

  // subscribe() keeps the first method it's given for this ref, so the subscriptions
  // go through this to always reach the current one (e.g. after id changes).
  const authChangedRef = useRef(authChanged);
  useEffect(() => {
    authChangedRef.current = authChanged;
  }, [authChanged]);

  useEffect(() => {
    api.subscription_holder.subscribe("Authenticated", mounted, () => authChangedRef.current());
    api.subscription_holder.subscribe("Reload", mounted, () => authChangedRef.current());
    api.subscription_holder.subscribe("Logout", mounted, () => {
      setObj(null);
      setPing(p => !p);
    });
  }, []);

  useEffect(() => {
    // In case login completed before the subscriptions were registered, and when id changes.
    load();
  }, [load]);

  const height = window.innerHeight - 64;
  if (!api.online) {
    return (
      <>Campaign Editing only works while online.  Try reconnecting.</>
    );
  } else if (loading) {
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else if (!api.user || !obj || obj._id !== (id ?? "")) {
    return (
      <span>This page is only visible to users who are in this campaign. If the browser is authenticating it should let you in in a moment if you are in the campaign.</span>
    );
  } else {
    return (
      // Keyed on the user so ending or starting a spoof remounts it - its tabs and
      // starting tab depend on whether the user is the GM.
      <CampaignParent key={`${obj._id}_${api.user._id}`} campaign={obj} />
    );
  }
}
